import React, { useEffect, useMemo, useState } from 'react'
import { useDrop } from 'react-dnd'
import styled, { css } from 'styled-components'
import { redirect } from 'utils/paths'
import { isNotNilOrEmpty } from 'utils/ramda'
import AnalyticProgressBar from 'features/analytics/compontents/AnalyticProgressBar'
import { attachAnalyticsTemplateRoutine } from 'features/analytics/ducks/actions'
import { selectAttachingTemplatesByBp } from 'features/analytics/ducks/selectors'
import { useDispatch, useSelector } from 'react-redux'
import { getCurrentUser } from 'features/auth/ducks/selectors'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import MenuItem from '@mui/material/MenuItem'
import Menu from '@mui/material/Menu'
import { toast } from 'react-toastify'
import { Skeleton, useMediaQuery, useTheme } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import EditOffIcon from '@mui/icons-material/EditOff'
import PersonIcon from '@mui/icons-material/Person'
import PeopleIcon from '@mui/icons-material/People'
import AttachTemplateOptionsModal from 'features/analytics/compontents/AttachTemplateOptionsModal'
import AttachTemplateInfoModal from 'features/analytics/compontents/AttachTemplateInfoModal'
import useBodyScrollLock from 'hooks/useBodyScrollLock'
import { getTemplateProvenanceLabel } from 'features/analytics/utils/templateProvenance'
import { canCurrentUserDetachTemplateFromBp } from 'features/analytics/utils/templateProvenance'
import { scoreColour } from 'features/dashboard/charts/scoreColour'

const getColor = scoreColour

const getTemplatePercent = template => {
  const rawValue =
    template?.percentageValue ??
    template?.percentage_value ??
    template?.pivot?.percentageValue ??
    template?.pivot?.percentage_value ??
    0

  const numericValue = Number(rawValue)
  return Number.isFinite(numericValue) ? numericValue : 0
}

const SegmentedDonut = ({ templates = [], size = 56, strokeWidth = 6, onSegmentClick }) => {
  const count = templates.length
  const completedCount = templates.filter(t => getTemplatePercent(t) === 100).length

  if (count === 0) {
    return (
      <div style={{ position: 'relative', width: size, height: size }}>
        <svg width={size} height={size}>
          <circle cx={size / 2} cy={size / 2} r={(size - strokeWidth) / 2} fill="none" stroke="#E0E0E0" strokeWidth={strokeWidth} />
        </svg>
        <CenterText>0/0</CenterText>
      </div>
    )
  }

  const radius = (size - strokeWidth) / 2
  const circumference = 2 * Math.PI * radius
  const gapLength = 4
  const segmentLength = count === 1 ? circumference : (circumference / count) - gapLength

  return (
    <div style={{ position: 'relative', width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ transform: 'rotate(-90deg)' }}>
        {templates.map((template, index) => {
          const percent = getTemplatePercent(template)
          const color = getColor(percent)
          const rotation = (360 / count) * index

          return (
            <circle
              key={template.id || index}
              cx={size / 2}
              cy={size / 2}
              r={radius}
              fill="none"
              stroke={color}
              strokeWidth={strokeWidth}
              strokeDasharray={count === 1 ? `${circumference} 0` : `${segmentLength} ${circumference - segmentLength}`}
              strokeDashoffset={0}
              transform={`rotate(${rotation} ${size / 2} ${size / 2})`}
              strokeLinecap={count > 1 ? "round" : "butt"}
              onClick={(e) => {
                e.stopPropagation()
                if (onSegmentClick) onSegmentClick(template)
              }}
              style={{ cursor: 'pointer' }}
            />
          )
        })}
      </svg>
      <CenterText onClick={(e) => {
        e.stopPropagation()
        if (onSegmentClick && templates.length > 0) onSegmentClick(templates[0])
      }}>
        {completedCount}/{count}
      </CenterText>
    </div>
  )
}

const CenterText = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 13px;
  font-weight: 700;
  color: #333;
  cursor: pointer;
`

// --- MAIN COMPONENT ---
const BpDropContainer = ({
  currentPage,
  bp,
  bps,
  index,
  handleOpenRemoveModal
}) => {
  const dispatch = useDispatch()
  const currentUser = useSelector(getCurrentUser)
  const [anchorEl, setAnchorEl] = useState(null)
  const dropdownOpen = Boolean(anchorEl)
  const isDormant = bp.status === 'dormant'

  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const [mobileExpanded, setMobileExpanded] = useState(false)
  const [selectedMobileTemplate, setSelectedMobileTemplate] = useState(null)
  const [attachOptionsOpen, setAttachOptionsOpen] = useState(false)
  const [attachInfoOpen, setAttachInfoOpen] = useState(false)
  const [pendingAttachVisibility, setPendingAttachVisibility] = useState('shared')
  const [pendingAttach, setPendingAttach] = useState(null)
  const attachingTemplatesByBp = useSelector(selectAttachingTemplatesByBp)

  useBodyScrollLock(isMobile && Boolean(selectedMobileTemplate))

  const getCreatorId = template =>
    template?.creator_id
    || template?.creatorId
    || template?.creator?.id
    || template?.created_by?.id
    || template?.createdBy?.id
    || template?.user_id
    || template?.userId

  const canEditTemplate = template => {
    const creatorId = getCreatorId(template)
    const isCreator = creatorId && currentUser?.id
      ? String(creatorId) === String(currentUser.id)
      : false
    const isAdmin = currentUser?.type === 'admin'
    const isLocked = !!(template?.locked ?? template?.is_locked ?? template?.isLocked ?? template?.pivot?.locked)
    return isCreator || isAdmin || !isLocked
  }

  const canDetachTemplate = template =>
    canCurrentUserDetachTemplateFromBp(template, currentUser, bp)

  const [{ isOver }, dropRef] = useDrop({
    accept: 'template',
    drop: (item, monitor) => {
      if (!monitor.didDrop() && !isDormant) {
        // Prevent drops on dormant BPs
        assignItem(item, bps[index])
      }
      return { containerIndex: index }
    },
    collect: monitor => ({
      isOver: !isDormant && monitor.isOver() // Only show hover state for non-dormant BPs
    })
  })

  const openMenu = e => {
    e.stopPropagation()
    setAnchorEl(e.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const assignItem = (template, buildingPassport) => {
    if (!canEditTemplate(template)) {
      toast.info('This template is non-editable (set by the creator).')
      return
    }
    const checklistTemplates = buildingPassport.checklistTemplates || buildingPassport.checklist_templates || []
    const findTemplate = checklistTemplates.find(
      temp => String(temp.id) === String(template.id)
    )

    if (!findTemplate) {
      setPendingAttach({ template, buildingPassport })
      setAttachOptionsOpen(true)
    } else {
      toast.error('This template is already assigned.')
    }
  }

  const handleAttachSubmit = ({ visibility }) => {
    if (!pendingAttach) return
    setPendingAttachVisibility(visibility || 'shared')
    setAttachOptionsOpen(false)
    setAttachInfoOpen(true)
  }

  const handleAttachClose = () => {
    setAttachOptionsOpen(false)
    setAttachInfoOpen(false)
    setPendingAttach(null)
  }

  const handleAttachInfoSubmit = () => {
    if (!pendingAttach) return
    dispatch(
      attachAnalyticsTemplateRoutine({
        page: currentPage,
        bpId: pendingAttach.buildingPassport.id,
        templateId: pendingAttach.template.id,
        visibility: pendingAttachVisibility || 'shared',
        item_tag_requirements: [],
        template: pendingAttach.template,
        attachedBy: currentUser
      })
    )
    setAttachInfoOpen(false)
    setPendingAttach(null)
  }

  const templatesSortedByType = useMemo(() => {
    const checklistTemplates = bp.checklistTemplates || bp.checklist_templates || []
    if (!isNotNilOrEmpty(checklistTemplates)) return []

    const visibleTemplates = checklistTemplates.filter(template => {
      if (template.visibility !== 'personal') return true

      const attachedById = template?.attached_by?.id
        || template?.attachedBy?.id
        || template?.attached_by_id

      if (!attachedById) return true

      return currentUser?.id && String(attachedById) === String(currentUser.id)
    })

    const defaults = visibleTemplates.filter(template => template.type === 'default')
    const customs = visibleTemplates.filter(template => template.type !== 'default')
    return [...defaults, ...customs]
  }, [bp, currentUser])

  const pendingTemplates = useMemo(
    () => attachingTemplatesByBp?.[String(bp.id)] || [],
    [attachingTemplatesByBp, bp.id]
  )
  const isBpAttaching = pendingTemplates.length > 0

  const visibleTemplates = useMemo(() => {
    const pendingIds = new Set(
      pendingTemplates.map(template => String(template?.id))
    )
    return templatesSortedByType.filter(
      template => !pendingIds.has(String(template?.id))
    )
  }, [pendingTemplates, templatesSortedByType])

  const handleMobileClick = () => {
    if (isMobile) {
      setMobileExpanded(!mobileExpanded)
    }
  }

  const handleSegmentClick = (template) => {
    if (isMobile) {
      setSelectedMobileTemplate(template)
    }
  }

  return (
    <PageWrapper>
      <FormContent
        ref={dropRef}
        isHighlighted={isOver}
        isDormant={isDormant}
        onClick={handleMobileClick}
        $isMobile={isMobile}
      >
        <Header>
          <InfoGroup>
            {isMobile && (
              <ChartWrapper>
                <SegmentedDonut
                  templates={templatesSortedByType}
                  onSegmentClick={handleSegmentClick}
                />
              </ChartWrapper>
            )}

            <TextContainer onClick={(e) => {
              if(!isMobile) {
                e.stopPropagation()
                redirect(`/bp-list/${bp.id}/floorplans`)
              }
            }}>
              <Title isDormant={isDormant}>{bp.name}</Title>
              <Address>
                {bp.street}, {bp.city}, {bp.postalCode}
              </Address>
            </TextContainer>
          </InfoGroup>

          <RightActions>
            {isMobile && (
              <ExpandIconWrapper>
                {mobileExpanded ? <ExpandLessIcon color="action" /> : <ExpandMoreIcon color="action" />}
              </ExpandIconWrapper>
            )}

            {!isDormant && !isMobile && (
              <IconWrapper>
                <MoreIcon onClick={openMenu} />
                <Menu
                  keepMounted
                  anchorEl={anchorEl}
                  style={{ marginTop: '10px' }}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                  open={dropdownOpen}
                  onClose={handleClose}
                >
                  <MenuItem onClick={() => redirect(`/bp-list/${bp.id}`, { path: 'guru' })}>
                    See Building Passport
                  </MenuItem>
                </Menu>
              </IconWrapper>
            )}
          </RightActions>
        </Header>

        {!isMobile && (
          <AssignedTemplatesContainer onClick={e => e.stopPropagation()}>
            {isBpAttaching ? (
              <PendingTemplateListSkeleton>
                {[1, 2, 3, 4, 5].map(item => (
                  <PendingTemplateRow key={`pending-progress-${bp.id}-${item}`}>
                    <div className='meta'>
                      <Skeleton variant='text' width='52%' height={28} />
                      <div className='badges'>
                        <Skeleton variant='rounded' width={70} height={22} />
                        <Skeleton variant='rounded' width={74} height={22} />
                        <Skeleton variant='circular' width={20} height={20} />
                      </div>
                    </div>
                    <div className='progress'>
                      <Skeleton variant='rounded' width='100%' height={6} />
                      <Skeleton variant='text' width={34} height={22} />
                    </div>
                    <div className='actions'>
                      <Skeleton variant='circular' width={20} height={20} />
                    </div>
                  </PendingTemplateRow>
                ))}
              </PendingTemplateListSkeleton>
            ) : (
              visibleTemplates.map(template => (
                <AnalyticProgressBar
                  currentPage={currentPage}
                  handleOpenRemoveModal={handleOpenRemoveModal}
                  key={`progress-${template.id}`}
                  template={template}
                  bp={bp}
                />
              ))
            )}
          </AssignedTemplatesContainer>
        )}

        {isMobile && mobileExpanded && (
          <MobileTemplatesList onClick={e => e.stopPropagation()}>
            {isBpAttaching ? (
              [1, 2, 3, 4].map(item => (
                <MobilePendingTemplateRow key={`pending-mobile-${bp.id}-${item}`}>
                  <div className='content'>
                    <Skeleton variant='text' width='42%' height={24} />
                    <div className='chips'>
                      <Skeleton variant='rounded' width={68} height={20} />
                      <Skeleton variant='rounded' width={72} height={20} />
                    </div>
                    <div className='progress-row'>
                      <Skeleton variant='rounded' width='100%' height={4} />
                      <Skeleton variant='text' width={28} height={18} />
                    </div>
                  </div>
                </MobilePendingTemplateRow>
              ))
            ) : visibleTemplates.map(template => {
              const percent = getTemplatePercent(template)
              const statusColor = getColor(percent)
              const provenanceLabel = getTemplateProvenanceLabel(template)

              return (
                <MobileTemplateRow
                  key={template.id}
                  onClick={() => setSelectedMobileTemplate(template)}
                >
                  <div className="content">
                    <div className="name-row">
                      <span className="name">{template.name}</span>
                    </div>
                    <MobileMetaBlock>
                      <MobileMetaInline>
                        {template.visibility && (
                          <MobileMetaItem $accent={template.visibility === 'personal' ? 'personal' : 'default'}>
                            {template.visibility === 'personal' ? <PersonIcon fontSize="inherit" /> : <PeopleIcon fontSize="inherit" />}
                            <span>{template.visibility === 'personal' ? 'Personal' : 'Shared'}</span>
                          </MobileMetaItem>
                        )}
                        {typeof template.locked === 'boolean' && template.locked && (
                          <MobileMetaItem $accent={template.locked ? 'locked' : 'default'}>
                            <EditOffIcon fontSize="inherit" />
                            <span>Non-editable</span>
                          </MobileMetaItem>
                        )}
                      </MobileMetaInline>
                      {provenanceLabel && (
                        <MobileByline title={provenanceLabel}>{provenanceLabel}</MobileByline>
                      )}
                    </MobileMetaBlock>

                    <div className="progress-row">
                      <div className="progress-track">
                        <div
                          className="progress-fill"
                          style={{ width: `${percent}%`, backgroundColor: statusColor }}
                        />
                      </div>
                      <span className="percent-text" style={{ color: statusColor }}>
                                        {Math.round(percent)}%
                                    </span>
                    </div>
                  </div>

                  {!isDormant && canDetachTemplate(template) && (
                    <RemoveBtn onClick={(e) => {
                      e.stopPropagation()
                      handleOpenRemoveModal(bp, template)
                    }}>
                      <CloseIcon style={{ fontSize: 18, color: '#bdbdbd' }} />
                    </RemoveBtn>
                  )}
                </MobileTemplateRow>
              )
            })}
            {visibleTemplates.length === 0 && !isBpAttaching && (
              <EmptyMobileMsg>No templates assigned.</EmptyMobileMsg>
            )}
          </MobileTemplatesList>
        )}
      </FormContent>

      {isMobile && (
        <>
          <Backdrop
            $isOpen={!!selectedMobileTemplate}
            onClick={() => setSelectedMobileTemplate(null)}
          />
          <MobileBottomSheet $isOpen={!!selectedMobileTemplate}>
            <SheetHeader>
              <SheetTitle>
                {selectedMobileTemplate?.name || 'Template Details'}
              </SheetTitle>
              <CloseIcon
                onClick={() => setSelectedMobileTemplate(null)}
                style={{ cursor: 'pointer', color: '#666' }}
              />
            </SheetHeader>

            <SheetContent>
              {selectedMobileTemplate && (
                <AnalyticProgressBar
                  currentPage={currentPage}
                  handleOpenRemoveModal={handleOpenRemoveModal}
                  key={`sheet-${selectedMobileTemplate.id}`}
                  template={selectedMobileTemplate}
                  bp={bp}
                  showDetailsOnly={true}
                  isDetailsView={true}
                />
              )}
            </SheetContent>
          </MobileBottomSheet>
        </>
      )}

      <AttachTemplateOptionsModal
        open={attachOptionsOpen}
        onClose={handleAttachClose}
        onSubmit={handleAttachSubmit}
        templateName={pendingAttach?.template?.name}
      />

      <AttachTemplateInfoModal
        open={attachInfoOpen}
        onClose={() => {
          setAttachInfoOpen(false)
          setAttachOptionsOpen(true)
        }}
        onSubmit={handleAttachInfoSubmit}
        templateName={pendingAttach?.template?.name}
      />

    </PageWrapper>
  )
}

export default BpDropContainer

const PageWrapper = styled.div`
  box-sizing: border-box;
  margin-bottom: 20px;
  width: 100%;
`

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: all 0.3s;
  width: 100%;
  margin-bottom: ${({ open }) => (open ? '20px' : '0')};
`

const InfoGroup = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
  gap: 16px;
`

const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  cursor: pointer;
  flex: 1;
  min-width: 0;

  @media (min-width: 769px) {
    flex-direction: row;
    align-items: baseline;
  }
`

const Address = styled.div`
  color: ${({ theme }) => theme.colors.grey[700]};
  font-size: 14px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (min-width: 769px) {
    margin-left: 10px;
  }
  @media (max-width: 768px) {
    margin-top: 4px;
    font-size: 13px;
    color: ${({ theme }) => theme.colors.black};
  }
`

const Title = styled.div`
  font-size: 22px;
  color: ${({ isDormant, theme }) =>
    !isDormant ? '#000' : theme.colors.grey[800]};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (max-width: 768px) {
    font-size: 16px;
    font-weight: 600;
    white-space: normal;
    overflow: visible;
  }
`

const FormContent = styled.div`
  box-sizing: border-box;
  width: 100%;
  transition: all 0.3s;
  background-color: ${({ isHighlighted, isDormant, theme }) =>
    isDormant ? theme.colors.grey[400] : isHighlighted ? '#d6f5f5' : 'white'};
  border-radius: 4px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.03);
  padding: 20px;
  cursor: ${({ isDormant }) => (isDormant ? 'default' : 'pointer')};

  ${({ $isMobile }) => $isMobile && css`
    padding: 16px;
    display: flex;
    flex-direction: column;
  `}
`

const AssignedTemplatesContainer = styled.div`
  margin: 20px 0;
`

const PendingTemplateListSkeleton = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const PendingTemplateRow = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 7px 0;

  .meta {
    width: 25%;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .badges {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .progress {
    width: 75%;
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .actions {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
  }
`

const MobileTemplatesList = styled.div`
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid #f0f0f0;
  display: flex;
  flex-direction: column;
`

const MobileTemplateRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 0;
  border-bottom: 1px solid #f9f9f9;
  cursor: pointer;
  transition: all 0.2s ease;

  &:last-child {
    border-bottom: none;
  }

  &:active {
    background-color: #fafafa;
    transform: scale(0.99);
  }

  .content {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-right: 16px;
  }

  .name-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .name {
    font-size: 14px;
    font-weight: 600;
    color: ${({ theme }) => theme.colors.haiti};
  }

  /* Randul cu bara si procentul */
  .progress-row {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .progress-track {
    flex: 1;
    height: 4px;
    background-color: #f0f0f0;
    border-radius: 2px;
    overflow: hidden;
    max-width: 150px;
  }

  .progress-fill {
    height: 100%;
    border-radius: 2px;
    transition: width 0.5s ease-out;
  }

  .percent-text {
    font-size: 12px;
    font-weight: 700;
    min-width: 30px;
    text-align: right;
  }
`

const MobilePendingTemplateRow = styled.div`
  padding: 12px 0;
  border-bottom: 1px solid #f9f9f9;

  .content {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .chips {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .progress-row {
    display: flex;
    align-items: center;
    gap: 10px;
  }
`

const MobileMetaInline = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
`

const MobileMetaBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
`

const MobileByline = styled.div`
  font-size: 11px;
  color: #6b7687;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const MobileMetaItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  line-height: 1.2;
  padding: 3px 8px;
  border-radius: 999px;
  border: 0;
  outline: 0;
  cursor: ${({ as }) => (as === 'button' ? 'pointer' : 'default')};
  background: ${({ $accent }) => {
    if ($accent === 'locked') return 'rgba(180, 63, 45, 0.12)'
    if ($accent === 'personal') return 'rgba(31, 108, 153, 0.12)'
    return 'rgba(79, 90, 106, 0.1)'
  }};
  color: ${({ $accent }) => {
    if ($accent === 'locked') return '#b43f2d'
    if ($accent === 'personal') return '#1f6c99'
    return '#4f5a6a'
  }};

  svg {
    font-size: 11px;
    opacity: 0.95;
  }
`

const RemoveBtn = styled.div`
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    transition: color 0.2s;
  }
  &:active svg {
    color: #D32F2F !important;
  }
`

const RightActions = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  margin-left: 10px;
`

const ChartWrapper = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
`

const ExpandIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`

const MoreIcon = styled(MoreVertIcon)`
  color: ${({ theme }) => theme.colors.grey[600]};
  cursor: pointer;
  font-size: 20px !important;
`

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
`

const Backdrop = styled.div`
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,0.5);
  z-index: 1300;
  opacity: ${({ $isOpen }) => $isOpen ? 1 : 0};
  pointer-events: ${({ $isOpen }) => $isOpen ? 'auto' : 'none'};
  transition: opacity 0.3s;
`

const MobileBottomSheet = styled.div`
  position: fixed;
  left: 0; right: 0; bottom: 0;
  background: white;
  border-radius: 20px 20px 0 0;
  padding: 20px;
  z-index: 1301;
  max-height: 85vh;
  overflow-y: auto;
  box-shadow: 0 -5px 20px rgba(0,0,0,0.2);
  transform: ${({ $isOpen }) => $isOpen ? 'translateY(0)' : 'translateY(100%)'};
  transition: transform 0.3s cubic-bezier(0.2, 0.8, 0.2, 1);
`

const SheetHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 10px;
  border-bottom: 1px solid #eee;
`

const SheetTitle = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.haiti};
`

const SheetContent = styled.div`
  /* Content container */
`

const EmptyMobileMsg = styled.div`
  padding: 10px 0;
  font-size: 13px;
  color: #999;
  font-style: italic;
  text-align: center;
`
